import { v4 as uuidv4 } from 'uuid';

import {
  AnalyticsEventPayload,
  AnalyticsProps,
  TrackKind,
  analyticsEventsBody,
  analyticsEventsUrl,
  postAnalyticsEvents,
} from '../api/analyticsApi/analyticsApi';
import { AnalyticsEvents } from './catalog';

/**
 * The one analytics pipeline of the web app: page views, clicks on tracked elements and
 * explicit events are queued here and sent to the API in batches.
 *
 * Grouping and order: the server groups events by the login session of the access token
 * (no session id is sent). Inside a session, each browser tab has a "visit" (a new one after
 * 30 minutes without activity) and every event gets the next sequence number, so the order in
 * one tab is exact. Every event carries its time by this browser's clock and every batch the
 * time it was sent, so the server can also store each time on its own clock (metadata.eventAt).
 *
 * Analytics never gets in the user's way: invalid events are not sent, and when sending fails
 * the events are dropped.
 */

const VISIT_STORAGE_KEY = 'analyticsVisit';
const VISIT_IDLE_MS = 30 * 60 * 1000;
const FLUSH_DELAY_MS = 5000;
/** The API accepts at most 50 events per request. */
const MAX_BATCH = 50;
/** Events kept while the API can't be reached; the oldest are dropped first. */
const MAX_QUEUE = 200;

const UI_CLICKED = 'ui.clicked';
const KINDS: readonly TrackKind[] = ['tile', 'link', 'button', 'tab', 'menu', 'toggle', 'other'];
/** The same rules as the API: an element id, and small flat details. */
const TARGET = /^[a-z0-9-]+(\.[a-z0-9-]+){1,7}$/;
const PROP_KEY = /^[A-Za-z][A-Za-z0-9_]{0,39}$/;
const MAX_PROPS = 10;
const PROP_STRING_MAX = 100;

/** A page as the app reports it: the route template and the record it is about. */
export interface PageInfo {
  page: string;
  subjectType?: string;
  subjectId?: string;
}

/** The record an explicit event is about, when it is not the page's. */
export interface Subject {
  type: string;
  id: string | number;
}

interface Visit {
  id: string;
  seq: number;
  lastAt: number;
  lastPage?: string;
}

let queue: AnalyticsEventPayload[] = [];
let flushTimer: number | undefined;
let started = false;
let resolvePage: (pathname: string) => PageInfo | undefined = () => undefined;

const accessToken = (): string | null => localStorage.getItem('access_token');

const warn = (message: string): void => {
  if (process.env.NODE_ENV !== 'production') {
    console.warn(`Analytics: ${message}`);
  }
};

export const isValidTarget = (target: string): boolean =>
  target.length <= 120 && TARGET.test(target);

export const isValidProps = (props: AnalyticsProps | undefined): boolean => {
  if (props === undefined) return true;
  const entries = Object.entries(props);
  return (
    entries.length <= MAX_PROPS &&
    entries.every(
      ([key, value]) =>
        PROP_KEY.test(key) &&
        (value === null ||
          typeof value === 'boolean' ||
          (typeof value === 'number' && Number.isFinite(value)) ||
          (typeof value === 'string' && value.length <= PROP_STRING_MAX)),
    )
  );
};

const readVisit = (): Visit | null => {
  try {
    const raw = sessionStorage.getItem(VISIT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Visit) : null;
  } catch {
    return null;
  }
};

const writeVisit = (visit: Visit): void => {
  try {
    sessionStorage.setItem(VISIT_STORAGE_KEY, JSON.stringify(visit));
  } catch {
    // Storage may be unavailable (private mode); tracking still works for this page.
  }
};

/**
 * Moves the tab's visit one step forward; sessionStorage keeps it across reloads of the tab.
 * Only a page view changes the "previous page" (clicks and events happen on a page).
 */
const nextVisitStep = (
  pageView?: string,
): Pick<AnalyticsEventPayload, 'visitId' | 'seq' | 'previousPage'> => {
  const now = Date.now();
  const current = readVisit();
  const visit: Visit =
    current && now - current.lastAt < VISIT_IDLE_MS
      ? current
      : { id: uuidv4(), seq: 0, lastAt: now };
  const next: Visit = {
    ...visit,
    seq: visit.seq + 1,
    lastAt: now,
    lastPage: pageView ?? visit.lastPage,
  };
  writeVisit(next);
  return {
    visitId: next.id,
    seq: next.seq,
    previousPage: pageView !== undefined ? visit.lastPage : undefined,
  };
};

const scheduleFlush = (): void => {
  if (flushTimer === undefined) {
    flushTimer = window.setTimeout(() => {
      flushTimer = undefined;
      void flushAnalytics();
    }, FLUSH_DELAY_MS);
  }
};

/** Queues one event (only while somebody is signed in) with its visit step and time. */
const enqueue = (
  event: Omit<AnalyticsEventPayload, 'visitId' | 'seq' | 'previousPage' | 'clientTime'>,
  pageView?: string,
): void => {
  if (!accessToken()) {
    return;
  }
  queue.push({ ...event, ...nextVisitStep(pageView), clientTime: new Date().toISOString() });
  if (queue.length > MAX_QUEUE) {
    queue = queue.slice(-MAX_QUEUE);
  }
  if (queue.length >= MAX_BATCH) {
    void flushAnalytics();
  } else {
    scheduleFlush();
  }
};

/** Set once by useAnalyticsTracking: turns a path into its route template and record. */
export const setPageResolver = (resolver: (pathname: string) => PageInfo | undefined): void => {
  resolvePage = resolver;
};

/** The route template (and record) of a path of this app, e.g. for a link's destination. */
export const pageOf = (pathname: string): PageInfo | undefined => resolvePage(pathname);

/** Records that the signed-in user opened a page. */
export const trackPageView = (view: PageInfo): void => {
  enqueue({ action: 'page.viewed', ...view }, view.page);
};

/**
 * Records a click on a tracked element (see clickTracker.ts), on the page shown now and about
 * its record. Invalid ids or details are not sent.
 */
export const trackClick = (click: {
  target: string;
  kind: TrackKind;
  props?: AnalyticsProps;
}): void => {
  const page = resolvePage(window.location.pathname);
  if (!page) return;
  if (!isValidTarget(click.target) || !isValidProps(click.props)) {
    warn(`click on "${click.target}" not recorded: invalid id or details`);
    return;
  }
  const props = click.props && Object.keys(click.props).length > 0 ? click.props : undefined;
  enqueue({
    action: UI_CLICKED,
    ...page,
    target: click.target,
    kind: KINDS.includes(click.kind) ? click.kind : 'other',
    ...(props ? { props } : {}),
  });
};

/**
 * Records an explicit event, e.g. trackEvent('cases.search.performed', { resultCount: 12 }).
 * The name and its details are declared in AnalyticsEvents (catalog.ts); the event is about
 * the page's record unless another one is given.
 */
export const trackEvent = <Name extends keyof AnalyticsEvents>(
  name: Name,
  details: AnalyticsEvents[Name],
  subject?: Subject,
): void => {
  const page = resolvePage(window.location.pathname);
  if (!page) return;
  const props = details as unknown as AnalyticsProps | undefined;
  if (!isValidProps(props)) {
    warn(`event "${String(name)}" not recorded: invalid details`);
    return;
  }
  enqueue({
    action: String(name),
    ...page,
    ...(subject ? { subjectType: subject.type, subjectId: String(subject.id) } : {}),
    ...(props && Object.keys(props).length > 0 ? { props } : {}),
  });
};

/** Sends the queued events now (through the API client, which refreshes the token if needed). */
export const flushAnalytics = async (): Promise<void> => {
  if (flushTimer !== undefined) {
    window.clearTimeout(flushTimer);
    flushTimer = undefined;
  }
  if (queue.length === 0 || !accessToken()) {
    return;
  }
  const batch = queue.splice(0, MAX_BATCH);
  try {
    await postAnalyticsEvents(batch);
  } catch {
    // Best effort: dropped.
  }
  if (queue.length > 0) {
    scheduleFlush();
  }
};

/**
 * Last send when the tab is hidden or closed. fetch with keepalive survives the page
 * unloading; navigator.sendBeacon can't send the Authorization header.
 */
const flushOnExit = (): void => {
  const token = accessToken();
  if (queue.length === 0 || !token) {
    return;
  }
  const batch = queue.splice(0, MAX_BATCH);
  fetch(analyticsEventsUrl(), {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(analyticsEventsBody(batch)),
  }).catch(() => undefined);
};

const onVisibilityChange = (): void => {
  if (document.visibilityState === 'hidden') {
    flushOnExit();
  }
};

/** Registers the tab-close listeners once; returns a cleanup function. */
export const startTracking = (): (() => void) => {
  if (started) {
    return () => undefined;
  }
  started = true;
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', flushOnExit);
  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', flushOnExit);
    started = false;
  };
};
